import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Send, Loader2, Heart, Megaphone,
  Users, Building2, Gift, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * /sponsor-a-mission — pitch + intake form for businesses who want to
 * back a Community Mission with their name on it. Flow:
 *
 *   1. Read the pitch (4 cards: who, why, what they get, how it works).
 *   2. Fill the intake form — POST /api/missions/sponsor-application.
 *   3. Backend lands the application in db.sponsor_mission_applications.
 *   4. Admin reviews from the Admin → Sponsor Missions tab and reaches out
 *      manually within 2 business days.
 *
 * No payment is taken on this page — this is a sales-qualification funnel,
 * not a self-serve checkout. We want a conversation before $$.
 */
export default function SponsorMissionPage() {
  return (
    <div className="splash-root" data-testid="sponsor-mission-page">
      <Hero />
      <ValueProps />
      <HowItWorks />
      <ApplicationForm />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="splash-hero relative">
      <div className="splash-hero-overlay" aria-hidden="true" />
      <div className="splash-grain" aria-hidden="true" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16 md:pb-24">
        <Link
          to="/"
          className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]"
          data-testid="sponsor-mission-back"
        >
          <ArrowLeft size={12} /> Back to The Local Loot
        </Link>
        <div className="mt-8 splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
          Sponsor a community mission
        </div>
        <h1 className="splash-display text-5xl md:text-7xl uppercase mt-3 leading-[0.95]">
          <span className="block headline-stamp">Put your name</span>
          <span className="block headline-stamp--green mt-1 md:mt-2">on the good stuff.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
          Community Missions are the non-cash, do-good drops that put us on the
          local news. Stock a food pantry. Plant a tree. Visit a senior center.
          When you sponsor one, your business name is on the mission, on the
          social posts, and on the Good Deeds wall — alongside every neighbor
          who showed up because of you.
        </p>
      </div>
    </section>
  );
}

function ValueProps() {
  const items = [
    { icon: Heart, title: "Goodwill that lasts", body: "Hunters remember the brand that backed the mission. So do their friends, and so does their algorithm." },
    { icon: Megaphone, title: "Authentic exposure", body: "Your name on the mission card, the Good Deeds wall, every social proof post, and every approved selfie caption." },
    { icon: Users, title: "Real foot traffic", body: "Many missions tie to a physical drop-off (food pantry, school supplies, library). That door is yours." },
    { icon: ShieldCheck, title: "We handle the work", body: "Admin approval, photo review, badge awards, posting. You pick the cause and write the check." },
  ];
  return (
    <section className="cream-section py-14 md:py-20" data-testid="sm-values">
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase text-center">
          Why sponsor a mission?
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((it, i) => {
            const I = it.icon;
            return (
              <article
                key={i}
                className="bg-[#fffaef] border-2 border-[#0d100d] p-5 md:p-6 shadow-[6px_6px_0_#0d100d]"
                data-testid={`sm-value-${i}`}
              >
                <span className="step-icon"><I size={22} /></span>
                <h3 className="splash-display text-xl md:text-2xl uppercase mt-3">{it.title}</h3>
                <p className="mt-2 text-[14px] text-[#0d100d]/75 leading-relaxed">{it.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Building2, title: "1. Tell us about your business", body: "Use the form below. Two minutes, max." },
    { icon: Gift, title: "2. We pitch a cause that fits", body: "Within 2 business days we propose a mission, a budget, and a launch window." },
    { icon: Megaphone, title: "3. We launch the mission", body: "Your logo, your story, your impact — everywhere we post it." },
    { icon: Heart, title: "4. The neighborhood shows up", body: "Hunters earn Good Deed badges; you earn the goodwill." },
  ];
  return (
    <section className="bg-[#0d100d] border-t border-[#76B82A]/15 border-b border-[#76B82A]/15 py-14 md:py-20" data-testid="sm-how">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="splash-display text-3xl md:text-5xl uppercase text-center">
          How it works
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((it, i) => {
            const I = it.icon;
            return (
              <article key={i} className="private-card" data-testid={`sm-step-${i}`}>
                <span className="pc-icon"><I size={20} /></span>
                <h3 className="splash-display text-xl md:text-2xl uppercase mt-3">{it.title}</h3>
                <p className="mt-2 text-[14px] text-[#f7f3e6]/75 leading-relaxed">{it.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ApplicationForm() {
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    cause: "", budget: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        cause: form.cause.trim(),
        budget: form.budget.trim() || undefined,
      };
      const { data } = await api.post("/missions/sponsor-application", payload);
      setDone(data?.message || "We got it. We'll be in touch within 2 business days.");
      toast.success("Pitch received");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" · ")
        : (typeof detail === "string" ? detail : "Couldn't submit. Try again in a moment.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="bg-[#0d100d] py-20 md:py-28" data-testid="sm-form-success">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A]">
            Sent
          </div>
          <h2 className="splash-display text-4xl md:text-6xl uppercase mt-3">
            <span className="headline-stamp--green">Talk soon.</span>
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
            {done}
          </p>
          <Link to="/" className="btn-loot-solid mt-8" data-testid="sm-form-success-back">
            Back to The Local Loot <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[#0d100d] py-16 md:py-24" data-testid="sm-form">
      <div className="max-w-2xl mx-auto px-4 md:px-8">
        <div className="text-center">
          <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
            Ready to back a mission?
          </div>
          <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2">
            Pitch us your business.
          </h2>
          <p className="mt-3 text-[15px] text-[#f7f3e6]/75">
            Two minutes. We read every one personally.
          </p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="sm-form-el">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Business name *">
              <input
                required minLength={2} maxLength={200}
                value={form.business_name}
                onChange={onChange("business_name")}
                className="lm-input" data-testid="sm-form-business"
              />
            </Field>
            <Field label="Your name *">
              <input
                required minLength={2} maxLength={120}
                value={form.contact_name}
                onChange={onChange("contact_name")}
                className="lm-input" data-testid="sm-form-contact"
              />
            </Field>
            <Field label="Email *">
              <input
                required type="email" maxLength={200}
                value={form.email}
                onChange={onChange("email")}
                className="lm-input" data-testid="sm-form-email"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                maxLength={40}
                value={form.phone}
                onChange={onChange("phone")}
                className="lm-input" data-testid="sm-form-phone"
              />
            </Field>
            <Field label="What cause are you drawn to? *" hint="10+ characters" className="md:col-span-2">
              <textarea
                required minLength={10} maxLength={2000} rows={5}
                value={form.cause}
                onChange={onChange("cause")}
                className="lm-input" data-testid="sm-form-cause"
                placeholder="Food security, animal shelter, school supplies, senior outreach, environmental cleanup…"
              />
            </Field>
            <Field label="Rough budget (optional)" className="md:col-span-2">
              <input
                maxLength={120}
                value={form.budget}
                onChange={onChange("budget")}
                className="lm-input" data-testid="sm-form-budget"
                placeholder="$250 / $500 / $1,000+ — we'll work with anything"
              />
            </Field>
          </div>
          <div className="pt-2">
            <button
              type="submit"
              disabled={busy}
              className="btn-loot-solid w-full justify-center"
              data-testid="sm-form-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {busy ? "Sending…" : "Send the pitch"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="flex items-baseline justify-between">
        <span className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A]">{label}</span>
        {hint && <span className="text-[10px] text-[#f7f3e6]/50">{hint}</span>}
      </div>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Footer() {
  return (
    <footer className="splash-footer py-8 text-center">
      <div className="max-w-7xl mx-auto px-4 md:px-8 splash-mono text-[12px] text-[#f7f3e6]/55">
        © The Local Loot · Live Life. Do Good. Be Happy.
      </div>
    </footer>
  );
}
